import React, { memo, useContext, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './index.module.scss';
import {
  apiV2EnergyOverviewDogConfigDetailPost,
  apiV2EnergyOverviewDogConfigListPost,
  apiV2EnergyOverviewRobotConfigDetailPost,
  apiV2EnergyOverviewRobotConfigListPost,
} from '@maxtropy/device-customer-apis-v2';
import CardBox from '../../../CardBox';
import AlarmStatistics from './components/AlarmStatistics';
import AlarmType from './components/AlarmType';
import InspectionRecorder from './components/InspectionRecorder';
import InspectionRobotPanel from './components/InspectionRobotPanel';
import AlarmArea from './components/AlarmArea';
import InspectionWorkOrder from './components/InspectionWorkOrder';
import EnvironmentalMonitor from './components/EnvironmentalMonitor';
import EnergySaving from './components/EnergySaving';
import EnergyResilienceModal from './components/EnergyResilienceModal';
import EnergyComparisonModal from './components/EnergyComparisonModal';
import { ShareDataUnityEnergyCockpitMapContext } from '../..';
import { ShareDataWrapperContext } from '../../../../index';
import dayjs from 'dayjs';
import { IndexTimeActive } from '../LeftBox/components/SwitchDate';
import { Modal } from '@maxtropy/components';
import bgDefault from '../../../../imgs/bg-areabutton-default@2x.png';
import bgHover from '../../../../imgs/bg-areabutton-hover@2x.png';
import classNames from 'classnames';
import robotImg from '../../../../imgs/icon-robot@2x.png';
import robotDogImg from '../../../../imgs/icon-robotDog@2x.png';
import FloatingMoreActions from '../../../FloatingMoreActions';

type InspectionType = 'recorder' | 'robot' | 'robotDog';

const modalButtons = [
  { key: 'energyComparison', label: '用能对比' },
  { key: 'inspectionWorkOrder', label: '巡检工单' },
  { key: 'environmentalMonitor', label: '环保监测' },
  { key: 'energySaving', label: '节能降碳' },
  { key: 'energyResilience', label: '韧性分析' },
];

const robotPanelProps = {
  title: '导轨机器人',
  icon: robotImg,
  iconAlt: '导轨机器人',
  titleFlex: '1 1 30%',
  listRequest: () => apiV2EnergyOverviewRobotConfigListPost({}),
  detailRequest: (id: number) => apiV2EnergyOverviewRobotConfigDetailPost({ id }),
};

const robotDogPanelProps = {
  title: '四足机器人',
  icon: robotDogImg,
  iconAlt: '四足机器人',
  titleFlex: '1 1 36%',
  listRequest: () => apiV2EnergyOverviewDogConfigListPost({}),
  detailRequest: (id: number) => apiV2EnergyOverviewDogConfigDetailPost({ id }),
};

const RightBox = () => {
  const { isStandardSize, loadMap } = useContext(ShareDataWrapperContext);
  const { timeResolution: activeKey, dateValue: value } = useContext(ShareDataUnityEnergyCockpitMapContext);
  const modalSizeProps = isStandardSize ? { size: 'big' as const } : { width: 1200 };
  const modalBodyStyle = { height: 580, maxHeight: 580 };
  const isLargeNoMapLayout = !isStandardSize && !loadMap;

  const [inspectionType, setInspectionType] = useState<InspectionType>('recorder');

  // Modal弹窗状态
  const [modalVisible, setModalVisible] = useState<{ [key: string]: boolean }>({
    energyComparison: false,
    inspectionWorkOrder: false,
    environmentalMonitor: false,
    energySaving: false,
    energyResilience: false,
  });

  const handleOpenModal = (key: string) => {
    setModalVisible(prev => ({ ...prev, [key]: true }));
  };

  const handleCloseModal = (key: string) => {
    setModalVisible(prev => ({ ...prev, [key]: false }));
  };

  // 渲染智能巡检内容
  const renderInspectionContent = () => {
    switch (inspectionType) {
      case 'recorder':
        return <InspectionRecorder />;
      case 'robot':
        return <InspectionRobotPanel key="robot" {...robotPanelProps} />;
      case 'robotDog':
        return <InspectionRobotPanel key="robotDog" {...robotDogPanelProps} />;
      default:
        return <InspectionRecorder />;
    }
  };

  // 渲染Modal内容
  const renderModalContent = (key: string) => {
    switch (key) {
      case 'energyComparison':
        return <EnergyComparisonModal />;
      case 'inspectionWorkOrder':
        return <InspectionWorkOrder visible={modalVisible.inspectionWorkOrder} />;
      case 'environmentalMonitor':
        return <EnvironmentalMonitor visible={modalVisible.environmentalMonitor} />;
      case 'energySaving':
        return <EnergySaving />;
      case 'energyResilience':
        return <EnergyResilienceModal />;
      default:
        return <div style={{ padding: 24, textAlign: 'center', color: 'rgba(255,255,255,0.65)' }}>功能开发中...</div>;
    }
  };

  // 渲染悬浮按钮（使用Portal渲染到不受transform影响的容器）
  const renderModalButtons = () => {
    if (isStandardSize && !loadMap) {
      return (
        <FloatingMoreActions
          actions={modalButtons.map(btn => ({
            key: btn.key,
            label: btn.label,
            onClick: () => handleOpenModal(btn.key),
          }))}
        />
      );
    }

    const container = document.getElementById('full_screen_box') ?? document.body;
    return createPortal(
      <div className={styles.modal_buttons_standard}>
        {modalButtons.map(btn => (
          <div
            key={btn.key}
            className={styles.area_btn_standard}
            style={{ backgroundImage: `url(${bgDefault})` }}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundImage = `url(${bgHover})`;
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundImage = `url(${bgDefault})`;
            }}
            onClick={() => handleOpenModal(btn.key)}
          >
            {btn.label}
          </div>
        ))}
      </div>,
      container
    );
  };

  // 非标准分辨率且地图加载时的布局
  if (!isStandardSize && loadMap) {
    return (
      <div className={styles.container_non_standard}>
        {/* 生产保障 */}
        <CardBox
          title="生产保障"
          className={styles.production_box}
          style={{
            width: 440,
            height: '50%',
            background: 'transparent',
            backdropFilter: 'none',
          }}
          extraContent={
            <span style={{ color: '#4A90E2' }}>
              {dayjs(value).format(activeKey === IndexTimeActive.THIS_MONTH ? 'YYYY-MM' : 'YYYY')}
            </span>
          }
        >
          <AlarmStatistics />
          <AlarmType />
        </CardBox>

        {/* 智能巡检 */}
        <CardBox
          title="智能巡检"
          className={styles.inspection_box}
          style={{
            width: 440,
            height: '50%',
            background: 'transparent',
            backdropFilter: 'none',
          }}
        >
          <div className={styles.tab_buttons}>
            <div
              className={`${styles.tab_btn} ${inspectionType === 'recorder' ? styles.active : ''}`}
              onClick={() => setInspectionType('recorder')}
            >
              巡检记录仪
            </div>
            <div
              className={`${styles.tab_btn} ${inspectionType === 'robot' ? styles.active : ''}`}
              onClick={() => setInspectionType('robot')}
            >
              导轨机器人
            </div>
            <div
              className={`${styles.tab_btn} ${inspectionType === 'robotDog' ? styles.active : ''}`}
              onClick={() => setInspectionType('robotDog')}
            >
              四足机器人
            </div>
          </div>
          {renderInspectionContent()}
        </CardBox>

        {/* 右侧盒子左下方的弹窗按钮 */}
        <div className={styles.modal_buttons}>
          {modalButtons.map(btn => (
            <div
              key={btn.key}
              className={styles.area_btn}
              style={{ backgroundImage: `url(${bgDefault})` }}
              onMouseEnter={e => {
                e.currentTarget.style.backgroundImage = `url(${bgHover})`;
              }}
              onMouseLeave={e => {
                e.currentTarget.style.backgroundImage = `url(${bgDefault})`;
              }}
              onClick={() => handleOpenModal(btn.key)}
            >
              {btn.label}
            </div>
          ))}
        </div>

        {/* Modal弹窗 */}
        {modalButtons.map(btn => {
          if (btn.key === 'energyResilience') {
            return (
              <Modal
                key={btn.key}
                title={btn.label}
                open={modalVisible[btn.key]}
                onCancel={() => handleCloseModal(btn.key)}
                footer={null}
                width={1400}
                bodyStyle={{ width: 1400, height: 672, maxHeight: 'none', padding: 0, overflow: 'hidden' }}
                destroyOnClose
                className={`${styles.customModal} ${!isStandardSize ? styles.largeModal : ''}`}
                getContainer={() => document.getElementById('full_screen_box') ?? document.body}
              >
                {renderModalContent(btn.key)}
              </Modal>
            );
          }

          if (btn.key === 'energyComparison') {
            return (
              <Modal
                key={btn.key}
                title={btn.label}
                open={modalVisible[btn.key]}
                onCancel={() => handleCloseModal(btn.key)}
                footer={null}
                width={1200}
                bodyStyle={{
                  height: 'calc(720px - 40px)',
                  maxHeight: 'calc(720px - 40px)',
                  padding: 0,
                  overflow: 'hidden',
                }}
                destroyOnClose
                className={`${styles.customModal} ${styles.energyComparisonModal} ${
                  !isStandardSize ? styles.largeModal : ''
                }`}
                getContainer={() => document.getElementById('full_screen_box') ?? document.body}
              >
                {renderModalContent(btn.key)}
              </Modal>
            );
          }

          return (
            <Modal
              key={btn.key}
              title={btn.label}
              open={modalVisible[btn.key]}
              onCancel={() => handleCloseModal(btn.key)}
              footer={null}
              {...modalSizeProps}
              bodyStyle={modalBodyStyle}
              destroyOnClose
              className={`${styles.customModal} ${!isStandardSize ? styles.largeModal : ''}`}
              getContainer={() => document.getElementById('full_screen_box') ?? document.body}
            >
              {renderModalContent(btn.key)}
            </Modal>
          );
        })}
      </div>
    );
  }

  // 原有布局（标准尺寸 ≤2560）
  return (
    <>
      <div className={classNames(styles.container, isLargeNoMapLayout ? styles.container_no_map : '')}>
        <CardBox
          title="生产保障"
          className={isLargeNoMapLayout ? styles.card_content_no_map : ''}
          style={{
            width: 0,
            height: isLargeNoMapLayout ? 'auto' : '100%',
            paddingRight: 14,
            borderRight: '1px dashed rgba(255, 255, 255, 0.2)',
            background: 'transparent',
            backdropFilter: 'none',
          }}
          extraContent={
            <span style={{ color: '#4A90E2' }}>
              {dayjs(value).format(activeKey === IndexTimeActive.THIS_MONTH ? 'YYYY-MM' : 'YYYY')}
            </span>
          }
        >
          <AlarmArea />
          <InspectionWorkOrder />
        </CardBox>
        <CardBox
          title="智能巡检"
          className={isLargeNoMapLayout ? styles.card_content_no_map : ''}
          style={{ flex: '0 0 300px', height: 'auto', background: 'transparent', backdropFilter: 'none' }}
        >
          <InspectionRecorder />
          <InspectionRobotPanel {...robotPanelProps} />
          <InspectionRobotPanel {...robotDogPanelProps} />
        </CardBox>

        {/* Modal弹窗 */}
        {modalButtons.map(btn => {
          if (btn.key === 'energyResilience') {
            return (
              <Modal
                key={btn.key}
                title={btn.label}
                open={modalVisible[btn.key]}
                onCancel={() => handleCloseModal(btn.key)}
                footer={null}
                width={1400}
                bodyStyle={{ width: 1400, height: 672, maxHeight: 'none', padding: 0, overflow: 'hidden' }}
                destroyOnClose
                className={`${styles.customModal} ${!isStandardSize ? styles.largeModal : ''}`}
                getContainer={() => document.getElementById('full_screen_box') ?? document.body}
              >
                {renderModalContent(btn.key)}
              </Modal>
            );
          }

          if (btn.key === 'energyComparison') {
            return (
              <Modal
                key={btn.key}
                title={btn.label}
                open={modalVisible[btn.key]}
                onCancel={() => handleCloseModal(btn.key)}
                footer={null}
                width={1200}
                bodyStyle={{
                  height: 'calc(720px - 40px)',
                  maxHeight: 'calc(720px - 40px)',
                  padding: 0,
                  overflow: 'hidden',
                }}
                destroyOnClose
                className={`${styles.customModal} ${styles.energyComparisonModal} ${
                  !isStandardSize ? styles.largeModal : ''
                }`}
                getContainer={() => document.getElementById('full_screen_box') ?? document.body}
              >
                {renderModalContent(btn.key)}
              </Modal>
            );
          }

          return (
            <Modal
              key={btn.key}
              title={btn.label}
              open={modalVisible[btn.key]}
              onCancel={() => handleCloseModal(btn.key)}
              footer={null}
              {...modalSizeProps}
              bodyStyle={modalBodyStyle}
              destroyOnClose
              className={`${styles.customModal} ${!isStandardSize ? styles.largeModal : ''}`}
              getContainer={() => document.getElementById('full_screen_box') ?? document.body}
            >
              {renderModalContent(btn.key)}
            </Modal>
          );
        })}
      </div>

      {/* 悬浮按钮 - 通过Portal渲染到不受transform影响的容器，固定在屏幕右下角 */}
      {renderModalButtons()}
    </>
  );
};

export default memo(RightBox);
